import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Field, FieldType } from '../../../../../service-groups/articles/article-details/field/field.model';
import { UtilsService } from '../../../../../core/utils/utils.service';

@Component({
    selector: 'app-job-additional-fields',
    templateUrl: './job-additional-fields.component.html',
    styleUrls: ['./job-additional-fields.component.scss'],
})
export class JobAdditionalFieldsComponent implements OnChanges {
    @Input() projectId: string;
    @Input() jobId: string;
    @Input() jobFormGroup: UntypedFormGroup; // the job formgroup
    @Input() changesAllowed = false;
    @Input() publicView = false;
    @Input() asDialog = false;

    fieldVisibilityMapping = []; // used to calculate if a field is visible or not

    constructor(public utils: UtilsService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.jobFormGroup) {
            // if the input form group changes we calculate the field visibility
            if (this.jobFormGroup != null) {
                this.fieldVisibilityMapping = [];
                this.calculateFieldVisibility();
            }
        }
    }

    /**
     * Helper function wich returns the fields for given job formGroup
     */
    getJobFieldsFormArray() {
        return this.jobFormGroup.get('fields') as UntypedFormArray;
    }

    /**
     * Calculates the field visibility for our current article
     */
    calculateFieldVisibility() {
        for (let fieldFormGroup of this.getJobFieldsFormArray().controls) {
            let fieldData = fieldFormGroup.value as Field;
            this.fieldVisibilityMapping[fieldData._id] = this.utils.checkFieldVisibilityCondition(
                fieldData,
                this.getJobFieldsFormArray().value
            );
            if (this.fieldVisibilityMapping[fieldData._id] === false) {
                // field is not visible, remove value if it is set, because we dont want invisible fields which have a value which could trigger other visibility conditions by mistake
                this.utils.resetFieldValueOfFieldFormGroup(fieldFormGroup);
            } else {
                // field is visible, check if it is a select field where we could auto-fill if we only have one option to choose from
                if (fieldData.type === FieldType.Select && fieldData.required && fieldData.possibleValues.length === 1) {
                    if (
                        fieldData.value == null ||
                        fieldData.value === '' ||
                        (Array.isArray(fieldData.value) && fieldData.value.length === 0)
                    ) {
                        // only set field.value if it currently is not set
                        if (fieldData.multiSelect) {
                            fieldFormGroup.get('value').setValue([fieldData.possibleValues[0].value]);
                        } else {
                            fieldFormGroup.get('value').setValue(fieldData.possibleValues[0].value);
                        }
                    }
                }
            }
        }
    }

    /**
     * Function to duplicate group field
     * @param idOfGroupField the id of the group field inside form array
     */
    onDuplicateGroup(idOfGroupField: string) {
        let foundArticleIndex = this.getJobFieldsFormArray().controls.findIndex((x) => x.get('_id').value === idOfGroupField);
        if (foundArticleIndex > -1) {
            // clone article data
            let foundArticleData = this.utils.clone(this.getJobFieldsFormArray().at(foundArticleIndex).value);
            let allDuplicatedFields = this.getJobFieldsFormArray().controls.filter((x) =>
                x.get('_id').value.startsWith(foundArticleData._id.split('|')[0])
            );

            // get last duplication Number
            let lastDuplicationNumber = allDuplicatedFields[allDuplicatedFields.length - 1].get('duplicationNumber').value;
            if (lastDuplicationNumber == null) {
                lastDuplicationNumber = 1;
            }

            foundArticleData.duplicationNumber = lastDuplicationNumber + 1;
            foundArticleData._id = `${foundArticleData._id.split('|')[0]}|${foundArticleData.duplicationNumber}`;
            foundArticleData.repetition = false;

            // create new form group and reset fields
            let foundArticle = this.utils.objectToFormGroup(foundArticleData);
            this.utils.resetFieldValueOfFieldFormGroup(foundArticle);

            // insert new article to form array
            let indexOfLastDuplicatedField = this.getJobFieldsFormArray().controls.findIndex(
                (x) => x.get('_id').value === allDuplicatedFields[allDuplicatedFields.length - 1].get('_id').value
            );
            if (indexOfLastDuplicatedField > -1) {
                this.getJobFieldsFormArray().insert(indexOfLastDuplicatedField + 1, foundArticle);
                this.calculateFieldVisibility();
            } else {
                console.error('error while duplicate group. Index to push: ', indexOfLastDuplicatedField);
            }
        }
    }

    /**
     * Function to delete duplicated group field
     * @param idOfGroupField the id of the group field inside form array
     */
    onDeleteDuplicatedGroup(idOfGroupField: string) {
        let foundArticleIndex = this.getJobFieldsFormArray().controls.findIndex((x) => x.get('_id').value === idOfGroupField);
        if (foundArticleIndex > -1) {
            this.getJobFieldsFormArray().removeAt(foundArticleIndex);
            this.calculateFieldVisibility();
        }
    }
}
